// src/components/MsoCalculationResult.tsx
//
// ★★ ANNEX A 22 — what the MSO calculator (and the enquiry detail) shows for ONE calculation:
// the answer, or the controlled refusal, with the rule and version that decided it, and the trace.
//
// ══ TWO CARDS, AND A REFUSAL IS NOT AN ERROR ══
//
// A refusal here is a RECORDED result, not a failure of the screen: the engine declined to price a
// bill it could not price correctly (`UNDEFINED_SCENARIO`, `INCOMPLETE_INPUT`, ...), said why, and
// stored the attempt. So the card names the kind in words AND by its code (an officer quoting it
// to a colleague, and a developer reading the log, both need the token), shows the engine's own
// message verbatim, lists the fields the refusal names, and STILL shows the calculation id and the
// trace. A refusal that dropped the trace would be a refusal nobody could explain.
//
// ⚠ Neither card re-derives a figure. The officer's and Government's amounts are the engine's
// own `officerCents` / `governmentCents`, rendered with `Money`.
import type { Ref } from 'react'
import { Link } from 'react-router'
import { Money } from './Money'
import { CategoryBadge } from './StatusBadge'
import { MsoTrace } from './MsoTrace'
import { msoRefusalLabel, msoRefusalNextStep } from '../lib/mso-vocabulary'
import type { MsoCalculateResponse } from '../lib/types'

/**
 * `headingRef` lets the calculator move focus to the result heading once it has rendered (tabindex -1, so
 * it is a focus target and not a tab stop): after Calculate by keyboard, focus lands on the answer instead of
 * being left on a control that has since re-rendered.
 */
export function MsoCalculationResult({ result, headingRef }: { result: MsoCalculateResponse; headingRef?: Ref<HTMLHeadingElement> }) {
  const { evaluation, calculation } = result
  const refused = evaluation.outcome === 'REFUSED'

  return (
    <div className="flex flex-col gap-4" data-testid="mso-result">
      {refused ? (
        <div id="mso-refusal" role="alert" className="pt-card">
          <p className="uppercase tracking-wide text-xs font-bold text-[var(--sgds-danger-color-emphasis)] mb-2">
            Not calculated: refused
          </p>
          <h2 ref={headingRef} tabIndex={-1} className="pt-result-heading text-lg font-semibold mb-1">
            <CategoryBadge kind="refusal" value={evaluation.refusal?.kind ?? 'UNKNOWN'} label={msoRefusalLabel(evaluation.refusal?.kind ?? 'UNKNOWN')} />
          </h2>
          {/* ⚠ The engine's own sentence, verbatim. */}
          <p className="text-sm mb-3">{evaluation.refusal?.message ?? 'The engine refused this calculation.'}</p>
          {/* F-30: a refusal says what to do next, not only what went wrong. */}
          <p className="text-sm mb-3 font-semibold" data-testid="refusal-next-step">
            What to do next: {msoRefusalNextStep(evaluation.refusal?.kind ?? 'UNKNOWN')}
          </p>
          {(evaluation.refusal?.fields.length ?? 0) > 0 && (
            <p className="text-sm mb-3">
              Fields named: <span className="pt-mono">{evaluation.refusal?.fields.map((f) => (f === '(unrecognised field)' ? 'an unrecognised field' : f)).join(', ')}</span>. They are marked in the
              form above.
            </p>
          )}
          <p className="text-xs text-[var(--sgds-body-color-subtle)]">
            No amount is shown. A bill is refused rather than priced on a guess, and this refusal is recorded as calculation{' '}
            <Link to={`/mso/calculations/${calculation.id}`} className="pt-mono">
              {calculation.id}
            </Link>
            .
          </p>
        </div>
      ) : (
        <div role="status" className="pt-card">
          <h2 ref={headingRef} tabIndex={-1} className="pt-result-heading uppercase tracking-wide text-xs font-bold text-[var(--sgds-color-subtle)] mb-2">
            <CategoryBadge kind="outcome" value="CALCULATED" label="Calculated" />
          </h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
            <dt className="text-sm font-semibold">Officer or dependant pays</dt>
            <dd className="font-semibold">
              <Money cents={evaluation.officerCents} />
            </dd>
            <dt className="text-sm font-semibold">Government pays</dt>
            <dd className="font-semibold">
              <Money cents={evaluation.governmentCents} />
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Rule</dt>
            <dd className="pt-mono">
              {evaluation.ruleVersionId === null ? (
                `${evaluation.ruleKey ?? ''} v${evaluation.ruleVersion ?? ''}`
              ) : (
                <Link to={`/administration/mso-rules/versions/${evaluation.ruleVersionId}`}>
                  {evaluation.ruleKey} v{evaluation.ruleVersion}
                </Link>
              )}
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Decision row</dt>
            <dd className="pt-mono">{evaluation.rowId ?? 'Not selected'}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Calculation</dt>
            <dd>
              <Link to={`/mso/calculations/${calculation.id}`} className="pt-mono">
                {calculation.id}
              </Link>
            </dd>
          </dl>
        </div>
      )}
      <MsoTrace trace={evaluation.trace} />
    </div>
  )
}
